import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Phone, Mail, ChevronDown, ExternalLink, Image, Calendar, Loader2, Pencil, Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getPhoneDisplay, getPhoneTel } from '@/lib/contactInfo';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', label: 'All Types' },
  { id: 'parades', label: 'Parades & Meet-and-Greet' },
  { id: 'samba', label: 'Samba Shows' },
  { id: 'cultural', label: 'Afro-Brasilian Cultural' },
  { id: 'schools', label: 'Schools & Kids' },
  { id: 'teambuilding', label: 'Team Building & Fitness' },
  { id: 'costumes', label: 'Costumes & Photo Ops' },
];

const PerformanceCard = ({ id, number, title, image, display_image, thumb_image, description, videoLinks = [], recommendations, timeSlots, category, delay = 0, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const { isAdminUser, enabled } = usePageEditMode();
  const adminEditing = isAdminUser && enabled;
  const bookingHref = `/booking?service=${encodeURIComponent(title)}`;
  const catLabel = CATEGORIES.find(c => c.id === category)?.label;

  // Detect the first Vimeo id from videoLinks so we can offer inline playback
  const vimeoId = (() => {
    for (const link of videoLinks) {
      const m = link.url && link.url.match(/vimeo\.com\/(\d+)/);
      if (m) return m[1];
    }
    return null;
  })();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className="bg-card border border-border/50 rounded-3xl overflow-hidden hover:border-primary/30 transition-all duration-500"
    >
      {/* Image/GIF Header - swaps to Vimeo player when Play is clicked */}
      <div className="relative aspect-video overflow-hidden bg-black">
        {playing && vimeoId ? (
          <>
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              data-testid={`vimeo-player-${number}`}
            />
            <a
              href={`https://vimeo.com/${vimeoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-2 right-2 z-20 px-3 py-1 rounded-full bg-black/70 hover:bg-primary hover:text-black text-white text-xs font-semibold transition-colors"
              data-testid={`vimeo-open-${number}`}
            >
              Open on Vimeo &rarr;
            </a>
          </>
        ) : (
          <>
            <img loading="lazy" decoding="async"
              src={display_image || image}
              alt={title}
              className="w-full h-full object-cover"
            />
            {vimeoId ? (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={`Play video for ${title}`}
                className="group absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/45 transition-colors"
                data-testid={`play-video-${number}`}
              >
                <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 group-hover:bg-primary shadow-2xl transition-transform group-hover:scale-110">
                  <Play className="w-7 h-7 md:w-9 md:h-9 text-black translate-x-0.5" fill="currentColor" />
                </span>
              </button>
            ) : null}
          </>
        )}
        <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-primary flex items-center justify-center text-black font-bold text-xl z-10">
          {number}
        </div>
        {catLabel ? (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-primary text-xs font-bold uppercase tracking-wider border border-primary/40 z-10">
            {catLabel}
          </div>
        ) : null}
      </div>
      
      {/* Content */}
      <div className="p-6 md:p-8">
        {adminEditing && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => onEdit && onEdit({ id, number, title, image, description, videoLinks, recommendations, timeSlots, category })}
              className="inline-flex items-center gap-1 text-xs font-semibold text-yellow-400 hover:text-yellow-300 border border-yellow-400/40 rounded-full px-3 py-1.5"
              data-testid={`edit-perf-${number}`}
              title="Edit image, category, and video links"
            >
              <Pencil className="w-3 h-3" /> Edit image / links
            </button>
            <button
              onClick={() => onDelete && onDelete(id, title)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-300 border border-red-400/40 rounded-full px-3 py-1.5 ml-auto"
              data-testid={`delete-perf-${number}`}
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        )}
        <h3 className="text-2xl md:text-3xl font-bold font-display text-foreground mb-4">
          <PageEditable path={`perf.${id}.title`} as="span" fallback={title} />
        </h3>
        
        <div className={`text-muted-foreground leading-relaxed ${expanded ? '' : 'line-clamp-4'}`}>
          <PageEditable path={`perf.${id}.description`} as="span" multiline fallback={description} />
        </div>
        
        {description.length > 300 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 mt-4 font-medium transition-colors"
          >
            {expanded ? 'Show Less' : 'Read More'}
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
        
        {/* Video/Gallery Links */}
        {videoLinks.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-6">
            {videoLinks.map((link, idx) => {
              const isInternal = link.url.startsWith('/');
              const isGallery = link.url.includes('gallery') || link.label.toLowerCase().includes('gallery');
              const IconComponent = isGallery ? Image : Play;
              
              if (isInternal) {
                return (
                  <Link
                    key={idx}
                    to={link.url}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full text-sm font-medium transition-colors"
                  >
                    <IconComponent className="w-4 h-4" />
                    <PageEditable path={`perf.${id}.videoLinks.${idx}.label`} as="span" fallback={link.label} />
                  </Link>
                );
              }
              
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full text-sm font-medium transition-colors"
                >
                  <IconComponent className="w-4 h-4" />
                  <PageEditable path={`perf.${id}.videoLinks.${idx}.label`} as="span" fallback={link.label} />
                </a>
              );
            })}
          </div>
        )}
        
        {/* Time Slots & Recommendations */}
        <div className="mt-6 pt-6 border-t border-border/30">
          {timeSlots && (
            <p className="text-sm text-muted-foreground mb-2">
              <span className="text-primary font-semibold">
                <PageEditable path="cardLabels.timeSlots" as="span" fallback="Time Slots:" />
              </span>{' '}
              <PageEditable path={`perf.${id}.timeSlots`} as="span" multiline fallback={timeSlots} />
            </p>
          )}
          {recommendations && (
            <p className="text-sm text-muted-foreground">
              <span className="text-primary font-semibold">
                <PageEditable path="cardLabels.recommended" as="span" fallback="Recommended for:" />
              </span>{' '}
              <PageEditable path={`perf.${id}.recommendations`} as="span" multiline fallback={recommendations} />
            </p>
          )}
        </div>

        {/* Quick Book CTA */}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={bookingHref}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-bold text-sm hover:scale-105 transition-transform shadow-md shadow-primary/30"
            data-testid={`book-this-${number}`}
          >
            <Calendar className="w-4 h-4" />
            <PageEditable path="cardLabels.bookBtn" as="span" fallback="Book This Performance" />
          </Link>
          <a
            href={getPhoneTel()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold text-sm transition-colors"
          >
            <Phone className="w-4 h-4" />
            <PageEditable path="cardLabels.callBtn" as="span" fallback="Call" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export const PerformanceDetails = () => (
  <PageEditProvider slug="performances">
    <PerformanceDetailsInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#performances-editor-btn" label="Edit Performances" />
  </PageEditProvider>
);

const PerformanceDetailsInner = () => {
  const { isAdminUser, enabled: pageEditEnabled } = usePageEditMode();
  const adminEditing = isAdminUser && pageEditEnabled;
  const [editModal, setEditModal] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const openEditModal = (p) => {
    setEditDraft({
      ...p,
      videoLinks: Array.isArray(p.videoLinks) ? [...p.videoLinks] : [],
    });
    setEditModal(p);
  };
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });
  const savePerformance = async () => {
    const videoLinks = (editDraft.videoLinks || [])
      .map((v) => ({ url: (v.url || '').trim(), label: (v.label || '').trim() }))
      .filter((v) => v.url);
    const patch = {
      title: editDraft.title,
      description: editDraft.description,
      image: editDraft.image,
      category: editDraft.category,
      videoLinks,
    };
    try {
      await axios.patch(`${API}/api/admin/performances/${editModal.id}`, patch, { headers: authHeaders() });
      const r = await axios.get(`${API}/api/performances`);
      setPerformances(r.data || []);
      toast.success('Performance saved');
      setEditModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  // Video link list helpers
  const updateVideoLink = (idx, field, value) => {
    setEditDraft((d) => {
      const next = [...(d.videoLinks || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...d, videoLinks: next };
    });
  };
  const addVideoLink = () => {
    setEditDraft((d) => ({ ...d, videoLinks: [...(d.videoLinks || []), { url: '', label: '' }] }));
  };
  const removeVideoLink = (idx) => {
    setEditDraft((d) => ({ ...d, videoLinks: (d.videoLinks || []).filter((_, i) => i !== idx) }));
  };
  const moveVideoLink = (idx, dir) => {
    setEditDraft((d) => {
      const arr = [...(d.videoLinks || [])];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return d;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...d, videoLinks: arr };
    });
  };
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/performances/${id}`, { headers: authHeaders() });
      setPerformances(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  const handleAdd = async () => {
    const title = window.prompt('New performance title?');
    if (!title) return;
    try {
      const nextNumber = Math.max(0, ...performances.map(p => p.number || 0)) + 1;
      await axios.post(`${API}/api/admin/performances`, {
        number: nextNumber, title, description: '', image: '',
        category: 'parades', videoLinks: [], active: true,
      }, { headers: authHeaders() });
      const r = await axios.get(`${API}/api/performances`);
      setPerformances(r.data || []);
      toast.success('Performance added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch performances from backend (source of truth)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/performances`);
        if (!cancelled) setPerformances(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load performances:', e);
        if (!cancelled) setPerformances([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Read ?filter=<category> deep-link from Homepage tiles
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get('filter');
    if (filter && CATEGORIES.some(c => c.id === filter)) {
      setActiveCategory(filter);
    }
  }, [location.search]);
  
  // Scroll to anchor on mount or hash change
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);


  return (
    <div className="min-h-screen">
      {/* Admin-only shortcut to the full editor at /admin/performances */}
      {isAdminUser && (
        <div
          className="w-full sticky top-0 z-40 border-b-4 border-yellow-400 bg-gradient-to-r from-red-600 via-amber-500 to-yellow-500 shadow-lg"
          data-testid="perf-admin-editor-bar"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
            <div className="min-w-0">
              <p className="text-black font-black text-sm sm:text-base leading-tight uppercase tracking-wide">
                Admin — Performance Details Editor
              </p>
              <p className="text-black/75 text-xs sm:text-sm leading-tight">
                Open the full editor to add / edit / delete performances and their video links.
              </p>
            </div>
            <a
              href="/admin/performances"
              data-testid="perf-open-editor-btn"
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-black hover:bg-neutral-900 text-yellow-300 font-bold text-sm flex items-center justify-center gap-2 whitespace-nowrap shadow-md"
            >
              Open Full Editor →
            </a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-10 md:py-12 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(28, 25, 23, 0.85), rgba(28, 25, 23, 0.95)), url(https://www.casasamba.com/wp-content/uploads/2022/03/NASPA-Parade-high-3.gif)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 rounded-full text-primary text-xs font-medium mb-3"
          >
            <PageEditable path="hero.eyebrow" fallback="CASA SAMBA INC." />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold font-display text-foreground mb-3"
          >
            <PageEditable path="hero.titlePre" fallback="Performance" /> <span className="text-primary"><PageEditable path="hero.titleAccent" fallback="Details" /></span>
          </motion.h1>
          <PageEditable
            path="hero.description"
            as="p"
            multiline
            className="text-base md:text-lg text-muted-foreground mb-5 max-w-2xl mx-auto"
            fallback="Discover our full range of authentic Afro-Brasilian performances, from energetic samba parades to spiritual Orixa presentations. 38 years of cultural excellence."
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <a 
              href={getPhoneTel()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-base transition-all hover:scale-105 shadow-glow"
            >
              <Phone className="w-4 h-4" />
              <PageEditable path="hero.phoneCtaPrefix" fallback="Book Now:" /> {getPhoneDisplay()}
            </a>
            <a 
              href="mailto:info@casasamba.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold text-base transition-all hover:scale-105"
            >
              <Mail className="w-4 h-4" />
              <PageEditable path="hero.emailCta" fallback="Contact Us" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Type Filter Chips */}
      <section className="sticky top-20 z-30 bg-background/95 backdrop-blur-lg border-b border-border/50 py-4 px-6" data-testid="type-filter-bar">
        <div className="max-w-[1700px] mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              <PageEditable path="filterBar.label" fallback="Browse by Type" />
            </span>
          </div>
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-1">
            {CATEGORIES.map(cat => {
              const count = cat.id === 'all'
                ? performances.length
                : performances.filter(p => p.category === cat.id).length;
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap transition-colors border ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border/50 hover:text-primary hover:border-primary/50'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.label}
                  <span className={`ml-2 text-[10px] font-bold ${active ? 'opacity-80' : 'opacity-60'}`}>({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Performance Cards */}
      <section className="py-16 px-6 xl:px-10">
        <div className="max-w-[1700px] mx-auto">
          {loading ? (
            <div className="flex flex-col items-center py-20 text-muted-foreground">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              <PageEditable path="loading" fallback="Loading performances..." />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {[...performances]
                .sort((a, b) => a.number - b.number)
                .filter(p => activeCategory === 'all' || p.category === activeCategory)
                .map((perf, idx) => (
                  <div key={perf.id || perf.number} id={`performance-${perf.number}`} className="scroll-mt-40">
                    <PerformanceCard {...perf} delay={idx * 0.05} onEdit={openEditModal} onDelete={handleDelete} />
                  </div>
                ))}
            </div>
          )}
          {adminEditing && !loading && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleAdd}
                className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 hover:bg-yellow-400/30 border-2 border-dashed border-yellow-400/70 rounded-2xl text-yellow-100 font-semibold"
                data-testid="add-performance-btn"
              >
                <Plus className="w-4 h-4" /> Add a new performance
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-background to-card">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center bg-card border border-border/50 rounded-3xl p-12"
        >
          <PageEditable
            path="cta.title"
            as="h2"
            className="text-3xl md:text-4xl font-bold font-display text-foreground mb-4"
            fallback="Ready to Book Escola de Samba Casa Samba?"
          />
          <PageEditable
            path="cta.description"
            as="p"
            multiline
            className="text-lg text-muted-foreground mb-8"
            fallback="Let us bring the energy and excitement of Brasilian Carnaval to your next event!"
          />
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href={getPhoneTel()}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow"
            >
              <Phone className="w-5 h-5" />
              {getPhoneDisplay()}
            </a>
            <a 
              href="https://www.casasamba.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold text-lg transition-all hover:scale-105"
            >
              <ExternalLink className="w-5 h-5" />
              <PageEditable path="cta.externalLabel" fallback="Visit CasaSamba.com" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* Admin: Edit performance modal */}
      {editModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85" onClick={() => setEditModal(null)}>
          <div className="w-full max-w-2xl bg-stone-900 border border-yellow-400/50 rounded-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-4">Edit performance #{editModal.number}</h3>
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="text-xs text-muted-foreground">Title</span>
                <input value={editDraft.title || ''} onChange={(e) => setEditDraft(d => ({ ...d, title: e.target.value }))} className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white" />
              </label>
              <label className="block text-sm">
                <span className="text-xs text-muted-foreground">Description</span>
                <textarea value={editDraft.description || ''} onChange={(e) => setEditDraft(d => ({ ...d, description: e.target.value }))} rows={4} className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white" />
              </label>
              <label className="block text-sm">
                <span className="text-xs text-muted-foreground">Image URL</span>
                <input value={editDraft.image || ''} onChange={(e) => setEditDraft(d => ({ ...d, image: e.target.value }))} className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white" />
                {editDraft.image && <img loading="lazy" decoding="async" src={editDraft.image} alt="preview" className="mt-2 h-32 rounded-lg object-cover" />}
              </label>
              <label className="block text-sm">
                <span className="text-xs text-muted-foreground">Category</span>
                <select value={editDraft.category || 'parades'} onChange={(e) => setEditDraft(d => ({ ...d, category: e.target.value }))} className="w-full mt-1 px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white">
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <div className="block text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Video Links</span>
                  <button
                    type="button"
                    onClick={addVideoLink}
                    data-testid="perf-video-add-btn"
                    className="px-2.5 py-1 rounded-md bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold"
                  >
                    + Add Video
                  </button>
                </div>
                {(editDraft.videoLinks || []).length === 0 && (
                  <p className="text-xs text-white/40 italic px-1">No videos yet. Click "+ Add Video" to add one.</p>
                )}
                <div className="space-y-2">
                  {(editDraft.videoLinks || []).map((link, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-stone-700 bg-stone-800/60 p-2.5 space-y-2"
                      data-testid={`perf-video-row-${idx}`}
                    >
                      <input
                        type="url"
                        value={link.url || ''}
                        onChange={(e) => updateVideoLink(idx, 'url', e.target.value)}
                        placeholder="https://vimeo.com/... or https://youtu.be/..."
                        className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-white text-sm"
                        data-testid={`perf-video-url-${idx}`}
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={link.label || ''}
                          onChange={(e) => updateVideoLink(idx, 'label', e.target.value)}
                          placeholder="Label (e.g. 'Highlight reel')"
                          className="flex-1 px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-white text-sm"
                          data-testid={`perf-video-label-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => moveVideoLink(idx, -1)}
                          disabled={idx === 0}
                          className="px-2 py-2 rounded-md bg-stone-700 hover:bg-stone-600 disabled:opacity-30 text-white text-xs"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveVideoLink(idx, +1)}
                          disabled={idx === (editDraft.videoLinks || []).length - 1}
                          className="px-2 py-2 rounded-md bg-stone-700 hover:bg-stone-600 disabled:opacity-30 text-white text-xs"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeVideoLink(idx)}
                          data-testid={`perf-video-remove-${idx}`}
                          className="px-3 py-2 rounded-md bg-red-500/80 hover:bg-red-500 text-white text-xs font-bold"
                          title="Remove"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditModal(null)} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
              <button onClick={savePerformance} className="px-4 py-2 text-sm bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceDetails;
